<template>
  <div
    class="ti-modal"
    :class="{
      'ti-modal--default': theme === ModalTheme.Default,
      'ti-modal--highlight': theme === ModalTheme.Highlight,
      'ti-modal--dark': theme === ModalTheme.Dark,
    }"
  >
    <a class="ti-modal__open-link" @click="showModal(true)" v-if="!unstyled">
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" fill-rule="evenodd">
          <path class="info-i-circle" d="M9 1a8 8 0 110 16A8 8 0 019 1z" />
          <path
            class="info-i-letter"
            d="M9.766 7.606H7.491v.926h1.053v3.376H7.407v.925h3.258v-.925h-.899zM8.995 4.833a.948.948 0 00-.941.955c0 .527.421.954.94.954.52 0 .942-.427.942-.954a.948.948 0 00-.941-.955z"
          />
        </g>
      </svg>

      <span v-if="label" v-html="$t(label)"></span>
    </a>

    <a
      class="ti-modal__unstyled-open-link"
      @click="showModal(true)"
      v-if="unstyled"
    >
      <span v-html="$t(label || '')"></span>
    </a>

    <Teleport :to="modalTarget" v-if="modalTarget">
      <wb-modal
        v-if="show"
        mode="info"
        ref="modalRef"
        @wbclose="closeModal"
        :close-on-backdrop-click="false"
        :close-on-escape-key="true"
        :scroll-lock="true"
      >
        <wb-modal-header></wb-modal-header>

        <wb-modal-content>
          <h2 class="ti-modal__headline">
            {{ $t(modal.headline) }}
          </h2>

          <p class="ti-modal__text" v-html="$t(modal.text)"></p>

          <wb-aspect-ratio
            class="ti-modal__image"
            :ratio="$t(`${modal.image.ratio}`)"
            v-if="modal.image"
          >
            <img
              :src="$parseSrc($t(modal.image.src))"
              :sizes="$t(modal.image.sizes as string)"
              :srcset="$t(modal.image.srcset as string)"
              :width="$t(modal.image.width as any)"
              :height="$t(modal.image.height as any)"
              :alt="$t(modal.image.alt as string)"
            />
          </wb-aspect-ratio>

          <a
            v-if="modal.closeLabel"
            class="wb-link wb-link--inline"
            @click="closeLabelClicked()"
          >
            {{ $t(modal.closeLabel) }}
          </a>
        </wb-modal-content>
      </wb-modal>
    </Teleport>
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { Modal, ModalTheme } from '../../utils/interfaces';
import setup from '../../composables/setup';

interface Props {
  modal: Modal;
  label?: string;
  theme?: ModalTheme;
  unstyled?: boolean;
}

const { $parseSrc } = setup();
const props = withDefaults(defineProps<Props>(), {
  theme: ModalTheme.Default,
});
const emit = defineEmits(['modalClosed']);
const show = ref(false);
const modalRef = ref();
const modalTarget = ref();

onMounted(() => {
  const tiElm = document.querySelector('tradein-app');
  const appElm = document.querySelector('#app');

  if (tiElm && tiElm.shadowRoot) {
    modalTarget.value = tiElm.shadowRoot.querySelector('#modal-target');
  } else {
    modalTarget.value = appElm?.querySelector('#modal-target');
  }
});

const showModal = (value: boolean) => {
  show.value = value;
};

const closeModal = async () => {
  await modalRef.value.runLeaveAnimation();
  showModal(false);
};

const closeLabelClicked = async () => {
  await closeModal();
  emit('modalClosed', props.modal);
};
</script>
<style lang="scss" src="./modal.scss"></style>
